/**
 * CoursAPartager.jsx — Page dédiée : liste TOUS les cours partageables
 * de la plateforme, avec un bouton QR par ligne pour projeter en classe.
 *
 * Route : /moniteur/cours-a-partager  (protégée MoniteurLayout)
 *
 * Chaque QR encode l'URL du cours suffixée avec ?share=c2r, ce qui permet
 * aux élèves NON inscrits de consulter le cours pendant leur session,
 * exactement comme via le ShareCourseButton flottant.
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Share2, X, Copy, Check, MessageCircle, QrCode, Search,
  Bike, Compass, Sparkles, BookMarked, GraduationCap, Car,
} from 'lucide-react';

const SHARE_TOKEN = 'c2r';

// ============================================================
// Catalogue complet des cours partageables — regroupés par catégorie.
// ============================================================
const CATALOGUE = [
  {
    key: 'plateau-a2',
    title: 'Plateau A2',
    icon: Compass,
    accent: 'from-rose-500/20 to-rose-500/5 border-rose-500/30 text-rose-300',
    dot: 'bg-rose-500',
    courses: [
      { path: '/cours/premiere-plateau-a2', label: '1ʳᵉ leçon plateau — Prise en main', duration: '2h' },
      { path: '/cours/deuxieme-plateau-a2', label: '2ᵉ leçon plateau — Maîtrise à allure réduite', duration: '2h' },
      { path: '/cours/troisieme-plateau-a2', label: '3ᵉ leçon plateau — Freinage & évitement', duration: '2h' },
      { path: '/cours/quatrieme-plateau-a2', label: '4ᵉ leçon plateau — Passage examen', duration: '2h' },
    ],
  },
  {
    key: 'circulation-a2',
    title: 'Circulation A2',
    icon: Bike,
    accent: 'from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-300',
    dot: 'bg-blue-500',
    courses: [
      { path: '/cours/premiere-circulation-a2', label: '1ʳᵉ circulation — Découverte du trafic', duration: '2h' },
      { path: '/cours/deuxieme-circulation-a2', label: '2ᵉ circulation — Intersections & priorités', duration: '2h' },
      { path: '/cours/troisieme-circulation-a2', label: '3ᵉ circulation — Route & vitesse', duration: '2h' },
      { path: '/cours/quatrieme-circulation-a2', label: '4ᵉ circulation — Voie rapide & autoroute', duration: '2h' },
      { path: '/cours/cinquieme-circulation-a2', label: '5ᵉ circulation — Nuit & conditions dégradées', duration: '2h' },
      { path: '/cours/sixieme-circulation-a2', label: '6ᵉ circulation — Bilan & examen blanc', duration: '2h' },
    ],
  },
  {
    key: 'permis-am',
    title: 'Permis AM · Option 1 · Cyclomoteur (14 ans)',
    icon: Sparkles,
    accent: 'from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-300',
    dot: 'bg-amber-500',
    courses: [
      { path: '/cours/permis-am-sensibilisation', label: 'S5 — Cérémonie de sensibilisation (45 min)', duration: '45 min', badge: 'STAR' },
      { path: '/cours/permis-am-journee-1', label: 'Journée 1 — Théorie & plateau', duration: '4h' },
      { path: '/cours/permis-am-journee-2', label: 'Journée 2 — Circulation supervisée', duration: '3h' },
      { path: '/permis-am/ceremonie', label: 'Cérémonie fin de formation AM (S5 fullscreen)', duration: '45-60 min' },
    ],
  },
  {
    key: 'permis-am-voiturette',
    title: 'Permis AM · Option 2 · Quadricycle léger (Voiturette)',
    icon: Car,
    accent: 'from-rose-500/20 to-rose-500/5 border-rose-500/30 text-rose-300',
    dot: 'bg-rose-500',
    courses: [
      { path: '/permis-am/voiturette', label: 'Hub Voiturette — 5 séquences officielles', duration: '8h', badge: 'STAR' },
      { path: '/permis-am/voiturette/representations', label: 'S1 — Représentations & attentes', duration: '30 min' },
      { path: '/permis-am/voiturette/hors-circulation', label: 'S2 — Formation hors circulation (plateau)', duration: '2h' },
      { path: '/permis-am/voiturette/code', label: 'S3 — Apports théoriques Code de la route', duration: '1h30' },
      { path: '/permis-am/voiturette/circulation', label: 'S4 — Conduite sur voies ouvertes', duration: '2h' },
      { path: '/permis-am/voiturette/sensibilisation', label: 'S5 — Sensibilisation aux risques', duration: '2h' },
      { path: '/permis-am/themes', label: '12 thèmes du permis AM (référentiel officiel)', duration: 'Ref' },
    ],
  },
  {
    key: 'formation-125',
    title: 'Formation 125 (7h)',
    icon: GraduationCap,
    accent: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-300',
    dot: 'bg-emerald-500',
    courses: [
      { path: '/formation-125/comprendre', label: 'Comprendre — Théorie 2h', duration: '2h' },
      { path: '/formation-125/maitriser', label: 'Maîtriser — Plateau 2h', duration: '2h' },
      { path: '/formation-125/anticiper', label: 'Anticiper — Circulation 3h', duration: '3h' },
    ],
  },
  {
    key: 'livrets',
    title: 'Livrets de progression',
    icon: BookMarked,
    accent: 'from-purple-500/20 to-purple-500/5 border-purple-500/30 text-purple-300',
    dot: 'bg-purple-500',
    courses: [
      { path: '/livret-plateau', label: 'Livret plateau A2 — Progression élève', duration: 'Interactif' },
      { path: '/livret-circulation', label: 'Livret circulation A2 — Progression élève', duration: 'Interactif' },
      { path: '/livret-125', label: 'Livret formation 125 (7h)', duration: 'Interactif' },
      { path: '/livret-am', label: 'Livret AM cyclomoteur', duration: 'Interactif' },
      { path: '/livrets/parcours-am', label: 'Livret pédagogique Permis AM Topolino (36 fiches)', duration: 'Interactif', badge: 'STAR' },
      { path: '/livrets/topolino-am', label: 'Livret de suivi Topolino — Mercredi & Samedi', duration: 'Interactif' },
      { path: '/briefing-circulation', label: 'Briefing séance circulation AM (mobile)', duration: '5 min' },
      { path: '/briefing-a2', label: 'Briefing circulation A2 (mobile)', duration: '5 min' },
    ],
  },
];

// ============================================================
// Modale QR — réutilisable pour n'importe quel cours du catalogue.
// ============================================================
function QRModal({ course, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!course) return null;

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = `${baseUrl}${course.path}?share=${SHARE_TOKEN}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) { /* ignore */ }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`📚 Voici le cours à consulter : ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="cours-share-modal"
    >
      <div
        className="bg-white rounded-3xl p-6 sm:p-7 max-w-sm w-full shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center active:scale-95 transition"
          aria-label="Fermer"
          data-testid="cours-share-close"
        >
          <X className="w-4 h-4 text-slate-700" strokeWidth={2.5} />
        </button>

        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-600 text-[10px] font-black uppercase tracking-widest mb-2">
            <Share2 className="w-3 h-3" /> Partage moniteur
          </div>
          <h2
            className="text-lg sm:text-xl font-black text-slate-900 leading-tight px-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {course.label}
          </h2>
          <p className="text-[11px] text-slate-500 mt-2">
            Tes élèves scannent le QR ci-dessous · aucun compte requis
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4 border-2 border-slate-100 flex items-center justify-center mb-4" data-testid="cours-share-qr">
          <QRCodeSVG
            value={shareUrl}
            size={220}
            level="M"
            includeMargin={false}
            fgColor="#0f172a"
            bgColor="#ffffff"
          />
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5 mb-3 flex items-center gap-2">
          <div className="flex-1 min-w-0 text-[10px] text-slate-600 font-mono truncate select-all">
            {shareUrl}
          </div>
          <button
            type="button"
            onClick={copyLink}
            className={`flex-shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
              copied ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-700 active:scale-95'
            }`}
            data-testid="cours-share-copy"
          >
            {copied ? <><Check className="w-3 h-3" /> Copié</> : <><Copy className="w-3 h-3" /> Copier</>}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={shareWhatsApp}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase tracking-widest text-[10px] px-3 py-2.5 active:scale-95 transition"
            data-testid="cours-share-whatsapp"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <Link
            to={course.path}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 hover:bg-slate-700 text-white font-black uppercase tracking-widest text-[10px] px-3 py-2.5 active:scale-95 transition"
            data-testid="cours-share-open"
          >
            Ouvrir le cours
          </Link>
        </div>

        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-[10px] text-amber-900 leading-relaxed">
          <strong>💡 À savoir :</strong> le lien fonctionne pour un élève NON inscrit tant qu&apos;il garde son onglet ouvert. Après fermeture, il devra créer un compte.
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Composant principal : la page /moniteur/cours-a-partager
// ============================================================
export default function CoursAPartager() {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');

  const totalCourses = useMemo(
    () => CATALOGUE.reduce((sum, cat) => sum + cat.courses.length, 0),
    []
  );

  const filteredCatalogue = useMemo(() => {
    if (!query.trim()) return CATALOGUE;
    const q = query.toLowerCase();
    return CATALOGUE
      .map((cat) => ({
        ...cat,
        courses: cat.courses.filter(
          (c) => c.label.toLowerCase().includes(q) || c.path.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.courses.length > 0);
  }, [query]);

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="cours-a-partager-page">
      {/* ===== HERO ===== */}
      <header className="rounded-3xl bg-gradient-to-br from-rose-950/60 via-[#1a0f14] to-[#0d0d0e] border border-rose-500/20 p-6 sm:p-8 shadow-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest">
            <Share2 className="w-3 h-3" /> Partage moniteur
          </div>
          <div className="text-white/40 text-[11px] font-mono">{totalCourses} cours disponibles</div>
        </div>
        <h1
          className="text-3xl sm:text-4xl font-black text-white leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Cours à partager
        </h1>
        <p className="text-white/70 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
          Sélectionne un cours ci-dessous, projette le QR au tableau : tes élèves scannent avec leur téléphone
          et accèdent instantanément au contenu — <strong className="text-rose-300">sans avoir à créer de compte</strong>.
        </p>
      </header>

      {/* ===== BARRE DE RECHERCHE ===== */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un cours (ex. plateau, AM, freinage)…"
          className="w-full h-12 pl-11 pr-4 rounded-2xl bg-[#141416] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20 transition"
          data-testid="cours-a-partager-search"
        />
      </div>

      {/* ===== LISTE PAR CATÉGORIE ===== */}
      {filteredCatalogue.length === 0 ? (
        <div className="rounded-2xl bg-[#141416] border border-white/10 p-8 text-center text-white/50 text-sm">
          Aucun cours ne correspond à ta recherche.
        </div>
      ) : (
        filteredCatalogue.map((cat) => {
          const CatIcon = cat.icon;
          return (
            <section key={cat.key} className="space-y-3" data-testid={`cours-cat-${cat.key}`}>
              <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-gradient-to-r ${cat.accent} border`}>
                <CatIcon className="w-4 h-4" />
                <span className="text-[12px] font-black uppercase tracking-widest">{cat.title}</span>
                <span className="text-[10px] opacity-60">· {cat.courses.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {cat.courses.map((c) => (
                  <div
                    key={c.path}
                    className="group flex items-center gap-3 p-3.5 rounded-2xl bg-[#141416] border border-white/10 hover:border-white/20 hover:bg-[#1a1a1c] transition"
                    data-testid={`cours-item-${c.path.replace(/\//g, '-')}`}
                  >
                    <div className={`shrink-0 w-2 h-10 rounded-full ${cat.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-white text-[13.5px] font-semibold truncate">{c.label}</div>
                        {c.badge && (
                          <span className="shrink-0 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            {c.badge}
                          </span>
                        )}
                      </div>
                      <div className="text-white/40 text-[11px] mt-0.5 font-mono truncate">
                        {c.duration} · {c.path}
                      </div>
                    </div>
                    <Link
                      to={c.path}
                      className="hidden sm:inline-flex items-center gap-1 shrink-0 h-9 px-3 rounded-xl border border-white/10 text-white/70 hover:text-white hover:bg-white/5 text-[11px] font-semibold transition"
                      data-testid={`cours-open-${c.path.replace(/\//g, '-')}`}
                    >
                      Voir
                    </Link>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-rose-900/40 active:scale-95 transition"
                      data-testid={`cours-share-${c.path.replace(/\//g, '-')}`}
                      aria-label={`Partager ${c.label}`}
                    >
                      <QrCode className="w-3.5 h-3.5" /> QR
                    </button>
                  </div>
                ))}
              </div>
            </section>
          );
        })
      )}

      {/* ===== INFO EN BAS ===== */}
      <div className="rounded-2xl bg-blue-500/5 border border-blue-500/20 p-4 text-[12px] text-blue-200 leading-relaxed">
        <strong className="text-blue-300">💡 Comment ça marche ?</strong> Le QR encode l&apos;URL du cours avec un jeton
        temporaire <code className="text-[11px] px-1.5 py-0.5 rounded bg-white/10">?share=c2r</code> qui autorise
        un accès session (tant que l&apos;élève garde son onglet ouvert). Idéal pour projeter en classe et laisser
        les élèves consulter sur leur téléphone.
      </div>

      {/* Modale QR */}
      <QRModal course={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
